import { Outlet } from "react-router-dom";
import { motion } from "framer-motion";
import { Award, ShieldPlus, Target, Trophy, type LucideIcon } from "lucide-react";
import { Logo } from "@/components/atoms/Logo";
import { FirebaseConfigWarning } from "@/components/organisms/FirebaseConfigWarning";
import { PageTransition } from "@/animations/PageTransition";

const FEATURES: Array<{ icon: LucideIcon; label: string }> = [
  { icon: ShieldPlus, label: "Crie ligas" },
  { icon: Trophy, label: "Convide amigos" },
  { icon: Target, label: "Faça palpites" },
  { icon: Award, label: "Dispute rankings" },
];

/**
 * Layout das telas públicas. Hero em gradiente com textura de "holofotes de
 * estádio" e bola flutuante — a identidade de futebol aparece já na primeira
 * tela, mesmo antes do login, conforme guia de marca. Cartão branco
 * flutuante para o formulário.
 */
export function AuthShell() {
  return (
    <div className="flex min-h-dvh flex-col bg-background">
      <FirebaseConfigWarning />

      <div className="stadium-glow relative flex shrink-0 flex-col items-center overflow-hidden bg-gradient-hero pb-8 pt-10 safe-top">
        <div className="absolute inset-0 bg-gradient-hero-radial" aria-hidden="true" />

        {/* bola flutuante decorativa */}
        <motion.div
          className="absolute right-8 top-6 opacity-25"
          animate={{ y: [0, -10, 0], rotate: [0, 15, 0] }}
          transition={{ duration: 5, repeat: Infinity, ease: "easeInOut" }}
          aria-hidden="true"
        >
          <svg width="46" height="46" viewBox="0 0 46 46" fill="none">
            <circle cx="23" cy="23" r="20" fill="#FFFFFF" />
            <path d="m23 12 6.5 4.7-2.5 7.6h-8l-2.5-7.6Z" fill="#0D47A1" />
          </svg>
        </motion.div>
        <motion.div
          className="absolute left-6 top-20 opacity-15"
          animate={{ y: [0, 8, 0], rotate: [0, -10, 0] }}
          transition={{ duration: 6, repeat: Infinity, ease: "easeInOut", delay: 0.5 }}
          aria-hidden="true"
        >
          <svg width="28" height="28" viewBox="0 0 46 46" fill="none">
            <circle cx="23" cy="23" r="20" fill="#FFFFFF" />
            <path d="m23 12 6.5 4.7-2.5 7.6h-8l-2.5-7.6Z" fill="#0D47A1" />
          </svg>
        </motion.div>

        <motion.div
          initial={{ opacity: 0, scale: 0.9 }}
          animate={{ opacity: 1, scale: 1 }}
          transition={{ duration: 0.4, ease: "easeOut" }}
          className="relative flex flex-col items-center gap-2"
        >
          <div className="rounded-2xl bg-white/12 p-3 backdrop-blur-sm">
            <Logo variant="mark" size={40} />
          </div>
          <span className="font-display text-2xl font-extrabold tracking-tight text-white">Palpiteiro Certo</span>
          <span className="text-sm font-medium text-white/70">O app dos apaixonados por futebol</span>
        </motion.div>

        <motion.div
          initial={{ opacity: 0, y: 8 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.3, delay: 0.15 }}
          className="relative mt-4 flex flex-wrap justify-center gap-1.5 px-6"
        >
          {FEATURES.map(({ icon: Icon, label }) => (
            <span
              key={label}
              className="inline-flex items-center gap-1 rounded-full bg-white/12 px-2.5 py-1 text-[11px] font-medium text-white backdrop-blur-sm"
            >
              <Icon className="h-3 w-3" aria-hidden="true" /> {label}
            </span>
          ))}
        </motion.div>

        {/* Curva de transição para o card do formulário */}
        <svg
          className="absolute -bottom-px left-0 w-full text-background"
          viewBox="0 0 400 32"
          preserveAspectRatio="none"
          aria-hidden="true"
        >
          <path d="M0 32V16C80 4 320 4 400 16V32H0Z" fill="currentColor" />
        </svg>
      </div>

      <div className="container -mt-1 flex-1 pb-10">
        <PageTransition>
          <Outlet />
        </PageTransition>
      </div>
    </div>
  );
}
